import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GenericTable } from "@shared/components/GenericTable";
import { PenBox, Search } from "lucide-react";
import {
  useMeterReadingStatusList,
  useUpdateMeterReadingStatus,
} from "../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";
import { Switch } from "@shared/ui/switch";
import { Button } from "@shared/ui/button";
import ReadingStatusAddEditForm from "./ReadingStatusAddEditForm";
import { PAGE_SIZE } from "@shared/utils/constants";
import { toast } from "@shared/ui/use-toast";
import { logEvent } from "@shared/analytics/analytics";

const MeterReadingStatus = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAddEditOpen, setIsAddEditOpen] = useState(false);
  const [details, setDetails] = useState();

  const currentPage = Number(searchParams.get("page")) || 1;
  const search = decodeURIComponent(searchParams.get("search") || "");

  const { data: statusList, isLoading } = useMeterReadingStatusList(
    remoteUtilityId,
    currentPage,
    PAGE_SIZE,
    search,
  );

  const { mutate: updateStatus } = useUpdateMeterReadingStatus();

  const toggleActive = (row: any) => {
    const { id, isActive } = row;
    updateStatus({
      id,
      payload: {
        is_active: !isActive,
      },
    }, {
      onSuccess: () => {
toast({
  title: "Success",
  description: "Activation status updated successfully",
  variant: "default",
});
        logEvent('MX Reading Status Toggled', {
          id,
          is_active: !isActive
        })
      },
      onError: () => {
toast({
  title: "Error",
  description: "Something went wrong",
  variant: "destructive",
});
      }
    });
  };

  const handleFormClose = () => {
    setIsAddEditOpen(false);
    setDetails(undefined);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...({ search: searchTerm }),
    };
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
  };

  const pagination = statusList
    ? {
        currentPage,
        totalPages: Math.ceil(statusList.count / PAGE_SIZE),
        totalItems: statusList.count,
        startItem: (currentPage - 1) * PAGE_SIZE + 1,
        endItem: Math.min(currentPage * PAGE_SIZE, statusList.count),
        itemsPerPage: PAGE_SIZE,
      }
    : undefined;

  const mappedData = statusList?.results;

  const columns: TableColumn<any>[] = [
    { key: "name", header: "Name" },
    { key: "reason", header: "Description" },
    { key: "createdDate", header: "Created Date" },
    { key: "createdUserRemoteName", header: "Created By" },
    { key: "codeStatus", header: "System Reading Status" },
    {
      key: "isActive",
      header: "Active",
      render: (value, row) => (
        <Switch
          checked={row.isActive}
          onCheckedChange={() => {
            toggleActive(row);
          }}
        />
      ),
    },
  ];

  const actions = [
    {
      label: "Edit",
      icon: <PenBox  className="h-5 w-5 text-muted-foreground" />,
      onClick: (row: any) => {
        setDetails(row);
        setIsAddEditOpen(true);
      },
    },
  ];

  return (
    <div className="space-y-4">
      <Button className="px-6 float-right" onClick={() => setIsAddEditOpen(true)}>
        Add
      </Button>
      <GenericTable
        data={mappedData}
        columns={columns}
        loading={isLoading}
        rowKey="name"
        onSearch={handleSearch}
        onSearchValueChange={handleSearch}
        actions={actions}
        actionsType="icons"
        searchPlaceholder="Search status..."
        searchDebounceMs={500}
        pagination={pagination}
        onPageChange={handlePageChange}
        emptyMessage="No status data found"
        emptyIcon={<Search className="h-8 w-8 text-muted-foreground mb-2" />}
      />
      <ReadingStatusAddEditForm
        open={isAddEditOpen}
        onOpenChange={handleFormClose}
        initialData={details}
      />
    </div>
  );
};

export default MeterReadingStatus;
